import React, { useEffect, useMemo, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend } from "recharts";
import { Info } from "lucide-react";
import usePulseLocale from "./i18n";
import { tokens, fmtCompact, fmtPct, fmtPeriodLong, MONTH_LABELS } from "./theme";
import { SkeletonCard, NoData, SourceChip, SectionTitle, SrTable,
  ChartTooltipContent } from "./components";
import { regionName } from "./regionNames";
import { pulseGet } from "./api";

/** P3 — Per-region seasonality (ISTAT monthly): yearly curves with the
 *  latest year highlighted, top-3-months concentration index, ISTAT lag
 *  exposed in the data-as-of chip. */
export default function ItalySeasonalityPanel({ regions, dm }) {
  const { t, lang } = usePulseLocale();
  const T = tokens(dm);
  const [region, setRegion] = useState("ITC1");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let on = true;
    setLoading(true);
    pulseGet(`italy/seasonality/${region}`)
      .then((d) => { if (on) setData(d); })
      .catch(() => { if (on) setData(null); })
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [region]);

  const chart = useMemo(() => {
    const years = data?.years || [];
    if (!years.length) return null;
    const rows = [];
    for (let m = 0; m < 12; m++) {
      const row = { month: MONTH_LABELS[lang][m] };
      years.forEach((y) => { row[y.year] = y.values[m]; });
      rows.push(row);
    }
    return { rows, yearKeys: years.map((y) => y.year) };
  }, [data, lang]);

  const latestYear = chart?.yearKeys[chart.yearKeys.length - 1];
  const conc = (data?.concentration || []).slice(-1)[0];
  const concPrev = (data?.concentration || []).slice(-2)[0];

  return (
    <section className="mb-10" data-testid="italy-seasonality-panel" aria-labelledby="italy-p3-title">
      <SectionTitle dm={dm} right={
        <select value={region} onChange={(e) => setRegion(e.target.value)}
          aria-label={t("it_p3_region_select")} data-testid="italy-seasonality-region-select"
          className="text-xs px-2 py-1.5 outline-none"
          style={{ background: T.surface, border: `1px solid ${T.border}`, color: T.text, borderRadius: 2 }}>
          {(regions || []).map((r) => (
            <option key={r.code} value={r.code}>{regionName(r.code, lang, r.label)}</option>
          ))}
        </select>
      }>
        <span id="italy-p3-title">{t("it_p3_title")}</span>
      </SectionTitle>

      {loading && <SkeletonCard h={300} dm={dm} />}
      {!loading && !chart && <NoData dm={dm} h={160} testid="italy-seasonality-no-data" />}
      {!loading && chart && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 p-4" style={{ background: T.surface, border: `1px solid ${T.border}` }}
            data-testid="italy-seasonality-chart">
            <div style={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chart.rows} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
                  <CartesianGrid stroke={T.border} vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 10, fill: T.muted }}
                    tickLine={false} axisLine={{ stroke: T.border }} />
                  <YAxis tickFormatter={(v) => fmtCompact(v, lang)}
                    tick={{ fontSize: 10, fill: T.muted }} tickLine={false} axisLine={false} width={48} />
                  <Tooltip content={<ChartTooltipContent dm={dm} formatter={(v) => fmtCompact(v, lang)} />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {chart.yearKeys.map((y, i) => {
                    const isLatest = y === latestYear;
                    return (
                      <Line key={y} type="monotone" dataKey={y} name={y}
                        stroke={isLatest ? T.brand : (dm ? "#6B7280" : "#9CA3AF")}
                        strokeOpacity={isLatest ? 1 : 0.35 + 0.12 * i}
                        strokeWidth={isLatest ? 2.5 : 1.25}
                        dot={false} connectNulls isAnimationActive animationDuration={400} />
                    );
                  })}
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-2">
              <SourceChip source="ISTAT" dataset={data.dataset_code}
                period={data.latest_month} dm={dm} testid="italy-seasonality-source" />
            </div>
            <SrTable caption={`${t("it_p3_title")} — ${regionName(region, lang, data.label)}`}
              columns={["Month", ...chart.yearKeys]}
              rows={chart.rows.map((r) => [r.month, ...chart.yearKeys.map((y) => fmtCompact(r[y], lang))])} />
          </div>

          <div className="p-4 flex flex-col gap-3" style={{ background: T.surface, border: `1px solid ${T.border}` }}
            data-testid="italy-concentration-card">
            <p className="text-[11px] font-semibold uppercase tracking-wider inline-flex items-center gap-1"
              style={{ color: T.muted }}>
              {t("it_p3_concentration")}
              <Info className="w-3 h-3 cursor-help" aria-label={t("it_p3_concentration_tip")}
                title={t("it_p3_concentration_tip")} />
            </p>
            {conc ? (
              <>
                <p className="pulse-num text-4xl font-semibold">
                  {fmtPct(conc.top3_share_pct, lang, false)}
                  <span className="text-xs font-normal ml-2" style={{ color: T.muted }}>{conc.year}</span>
                </p>
                {concPrev && concPrev.year !== conc.year && (
                  <p className="text-xs" style={{ color: T.muted }}>
                    {concPrev.year}: <span className="pulse-num">{fmtPct(concPrev.top3_share_pct, lang, false)}</span>
                  </p>
                )}
              </>
            ) : <NoData dm={dm} h={80} />}
            <p className="text-[11px] leading-relaxed mt-auto" style={{ color: T.muted }}
              data-testid="italy-seasonality-lag-note">
              {t("it_p3_lag_note", { period: fmtPeriodLong(data.latest_month, lang) })}
            </p>
          </div>
        </div>
      )}
    </section>
  );
}
